import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, MessageSquareWarning } from "lucide-react";
import type { DisconnectConversationChoice } from "@/lib/conversation-integration-assign";

export type DisconnectIntegrationTarget = {
  id: string;
  provider: string;
  status: string | null;
  label: string;
  isHardDelete: boolean;
};

export type ReassignIntegrationOption = {
  id: string;
  label: string;
  detail?: string;
};

type ConversationHandlingMode = "reassign" | "unassigned" | "keep";

interface DisconnectIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DisconnectIntegrationTarget | null;
  conversationCount: number;
  reassignOptions: ReassignIntegrationOption[];
  onConfirm: (choice: DisconnectConversationChoice) => Promise<void>;
}

export function DisconnectIntegrationDialog({
  open,
  onOpenChange,
  target,
  conversationCount,
  reassignOptions,
  onConfirm,
}: DisconnectIntegrationDialogProps) {
  const [mode, setMode] = useState<ConversationHandlingMode>("unassigned");
  const [targetIntegrationId, setTargetIntegrationId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canReassign = reassignOptions.length > 0;
  const canKeep = Boolean(target && !target.isHardDelete);
  const hasConversations = conversationCount > 0;

  useEffect(() => {
    if (!open) return;
    if (canReassign) {
      setMode("reassign");
      setTargetIntegrationId(reassignOptions[0]?.id ?? "");
    } else if (canKeep) {
      setMode("keep");
    } else {
      setMode("unassigned");
    }
  }, [open, canReassign, canKeep, reassignOptions]);

  const buildChoice = (): DisconnectConversationChoice => {
    if (mode === "reassign" && targetIntegrationId) {
      return { mode: "reassign", targetIntegrationId };
    }
    if (mode === "keep") {
      return { mode: "keep" };
    }
    return { mode: "unassigned" };
  };

  const handleConfirm = async () => {
    if (mode === "reassign" && !targetIntegrationId) return;
    setSubmitting(true);
    try {
      await onConfirm(buildChoice());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = target?.isHardDelete ? "Remover integração" : "Desconectar";

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareWarning className="h-5 w-5 text-amber-600" />
            {actionLabel}: {target?.label ?? "conexão"}
          </DialogTitle>
          <DialogDescription>
            {hasConversations ? (
              <>
                Existem{" "}
                <strong className="text-foreground font-medium">
                  {conversationCount} conversa{conversationCount === 1 ? "" : "s"}
                </strong>{" "}
                vinculada{conversationCount === 1 ? "" : "s"} a esta conexão. Escolha abaixo para que o
                histórico não desapareça da lista.
              </>
            ) : target?.isHardDelete ? (
              "Não há conversas vinculadas. A integração será removida permanentemente."
            ) : (
              "Não há conversas vinculadas. A conexão será desativada."
            )}
          </DialogDescription>
        </DialogHeader>

        {hasConversations ? (
          <div className="space-y-4 py-1">
            <Label className="text-sm font-medium">O que fazer com as conversas vinculadas?</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as ConversationHandlingMode)}
              className="space-y-3"
            >
              {canReassign ? (
                <ChoiceRow
                  value="reassign"
                  id="disconnect-reassign"
                  title="Reatribuir a outra conexão"
                  description="As conversas passam a aparecer no número ou conta escolhida."
                >
                  <Select
                    value={targetIntegrationId}
                    onValueChange={setTargetIntegrationId}
                    disabled={mode !== "reassign" || submitting}
                  >
                    <SelectTrigger className="mt-2 h-9">
                      <SelectValue placeholder="Selecione a conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      {reassignOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label}
                          {opt.detail ? ` · ${opt.detail}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ChoiceRow>
              ) : null}

              <ChoiceRow
                value="unassigned"
                id="disconnect-unassigned"
                title="Deixar em aberto"
                description="Conversas ficam no canal sem número ou conta fixo. Nada é apagado."
              />

              {canKeep ? (
                <ChoiceRow
                  value="keep"
                  id="disconnect-keep"
                  title="Manter nesta conexão (desconectada)"
                  description="Histórico continua vinculado a este número desconectado até reatribuir depois."
                />
              ) : null}
            </RadioGroup>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant={target?.isHardDelete ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={submitting || (mode === "reassign" && !targetIntegrationId)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChoiceRow({
  value,
  id,
  title,
  description,
  children,
}: {
  value: string;
  id: string;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <RadioGroupItem value={value} id={id} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="font-medium cursor-pointer">
          {title}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {children}
      </div>
    </div>
  );
}
